import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { TableCell, TableRow } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import Content from 'components/content/content'
import Filter from 'components/filter/filter'
import CONSTANT from 'modules/constant'
import GLOBAL from 'modules/global'
import type { ErrorProps } from 'modules/types'
import type { TestDriveHistory, PartialTestDriveHistory } from 'modules/testdrive/test-drive.dto'
import Notification from 'components/notification'
import { useGetTestDriveListMutation, useDownloadTestDriveListMutation } from 'store/testDrive'

const TestDriveList = () => {
  const [getTestDriveList, testDriveList] = useGetTestDriveListMutation()
  const [downloadTestDriveList, download] = useDownloadTestDriveListMutation()

  const [payload, setPayload] = useState<PartialTestDriveHistory>({
    start: 0,
    length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    search: ''
  })

  const [filter, setFilter] = useState({
    submissionDateFrom: '',
    submissionDateTo: ''
  })

  const [dialogFilter, setDialogFilter] = useState<boolean>(false)
  const [dateError, setDateError] = useState<boolean>(false)

  const formatDate = (e: string) => (!isEmpty(e) ? moment(e).format('DD-MM-YYYY HH:mm') : '-')
  const onSearch = (value: string) => setPayload({ ...payload, start: 0, search: value })
  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 })
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value })

  const getField = () =>
    columns
      .filter((c) => c.searchable !== false)
      .map((c) => c.key)
      .join(',')

  const buildFormData = (overrides?: Partial<PartialTestDriveHistory>) => {
    const merged = { ...payload, ...overrides }
    const formData = new FormData()

    formData.append('start', merged.start.toString())
    formData.append('length', merged.length.toString())
    formData.append('field', getField())

    if (!isEmpty(merged.search)) formData.append('search[value]', merged.search)
    if (merged.submissionDateFrom) formData.append('submissionDateFrom', merged.submissionDateFrom)
    if (merged.submissionDateTo) formData.append('submissionDateTo', merged.submissionDateTo)

    return formData
  }

  const onResync = () => getTestDriveList(buildFormData())

  const onDownload = (type?: string) => {
    const formData = buildFormData({ start: 0, length: CONSTANT.DEFAULT_PAGINATION_MAXIMUM_DOWNLOAD })
    if (type && !isEmpty(type)) formData.append('Download-Type', type)
    downloadTestDriveList(formData)
  }

  const onFilterSubmit = () => {
    setPayload({ ...payload, start: 0, ...filter })
    setDialogFilter(false)
  }

  const onFilterReset = () => {
    setFilter({ submissionDateFrom: '', submissionDateTo: '' })
    setPayload({ ...payload, start: 0, submissionDateFrom: '', submissionDateTo: '' })
    setDialogFilter(false)
  }

  const columns = [
    { column: 'Member Code', label: 'Member Code', searchable: true, key: 'memberCode' },
    { column: 'Customer Name', label: 'Customer Name', searchable: true, key: 'customerName' },
    { column: 'Cust Email', label: 'Cust Email', searchable: true, key: 'email' },
    { column: 'Cust Phone', label: 'Cust Phone', searchable: true, key: 'phoneNumber' },
    { column: 'Car Model', label: 'Car Model', searchable: true, key: 'carModel' },
    { column: 'Dealer', label: 'Dealer', searchable: true, key: 'dealer' },
    {
      column: 'Preferred Date',
      label: 'Preferred Date',
      key: 'preferredDate',
      searchable: false,
      render: (row: TestDriveHistory) => formatDate(row.preferredDate)
    },
    {
      column: 'Preferred Time',
      label: 'Preferred Time',
      key: 'preferredTime',
      searchable: false,
      render: (row: TestDriveHistory) => (!isEmpty(row.preferredTime) ? moment(row.preferredTime).format('HH:mm') : '-')
    },
    {
      column: 'Form Submission Date',
      label: 'Form Submission Date',
      key: 'formSubmissionDate',
      searchable: false,
      render: (row: TestDriveHistory) => formatDate(row.formSubmissionDate)
    }
  ]

  const tableHeadTitles = columns.map((c) => c.label)

  const totalPage = testDriveList.data && GLOBAL.tableTotalPage(testDriveList.data.recordsTotal)
  const numbers = GLOBAL.tableNumber(payload.start)

  useEffect(() => {
    onResync()
  }, [payload])

  return (
    <>
      <Content
        title='Test Drive Submissions'
        tableHeadTitles={tableHeadTitles}
        onSearch={onSearch}
        onResync={onResync}
        onFilter={() => setDialogFilter(true)}
        onDownload={onDownload}
        downloadLoading={download.isLoading}
        onDownloadTypeIgnored={['pdf']}
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        totalRecords={testDriveList.data && testDriveList.data.recordsTotal}
        page={payload.start + 1}
        isLoading={testDriveList.isLoading}
        useAction={false}
        useDownloadDropdown
      >
        {testDriveList.isSuccess &&
          testDriveList.data &&
          testDriveList.data.data?.map((table, index) => (
            <TableRow key={table.id}>
              <TableCell>{numbers[index] + 1}</TableCell>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  {col.render
                    ? col.render(table as TestDriveHistory)
                    : table[col.key as keyof TestDriveHistory] || '-'}
                </TableCell>
              ))}
            </TableRow>
          ))}
      </Content>

      <Filter
        open={dialogFilter}
        title='Filter Test Drive'
        isSubmitDisabled={dateError}
        onCancel={() => setDialogFilter(false)}
        onReset={onFilterReset}
        onSubmit={onFilterSubmit}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
          <DatePicker
            value={filter.submissionDateFrom ? dayjs(filter.submissionDateFrom) : null}
            onChange={(val: Dayjs | null) => {
              setFilter({ ...filter, submissionDateFrom: val ? val.format('YYYY-MM-DD') : '' })
              setDateError(false)
            }}
            label='Form Submission Start Date'
            format='DD-MM-YYYY'
            sx={{ width: '100%', marginBottom: '1rem' }}
          />
          <DatePicker
            value={filter.submissionDateTo ? dayjs(filter.submissionDateTo) : null}
            minDate={filter.submissionDateFrom ? dayjs(filter.submissionDateFrom) : undefined}
            onChange={(val: Dayjs | null) => {
              setFilter({ ...filter, submissionDateTo: val ? val.format('YYYY-MM-DD') : '' })
              setDateError(false)
            }}
            onError={(err) => setDateError(!!err)}
            label='Form Submission End Date'
            format='DD-MM-YYYY'
            sx={{ width: '100%' }}
          />
        </LocalizationProvider>
      </Filter>

      <Notification
        open={testDriveList.isError}
        onClose={() => testDriveList.reset()}
        isError={testDriveList.isError}
        message={GLOBAL.returnExceptionMessage(
          testDriveList.isError,
          testDriveList.error as ErrorProps
        )}
      />
    </>
  )
}

export default TestDriveList
